/** 销售订单列表/详情展示字段投影（从 order-detail 全量 payload 选取） */

import { fixedUi } from '@/i18n/fixedUi';
import {
  SALES_ORDER_STATUS_LABELS,
  SALES_ORDER_STOCK_MODE_LABELS,
} from '@/temu-platform/constants/temuApiCatalog';
import {
  displayValue,
  formatMoney,
  mapLabel,
  normalizeImageUrl,
} from '@/temu-platform/modules/aftersales/returnOrderDisplay';
import { pickField } from '@/temu-platform/utils/temuResponse';

import i18n from '@/i18n';

const ORDER_TEXT = {
  "预计总收入": "income",
  "商品总金额": "productTotal",
  "供货价合计": "costTotal",
  "买家运费": "buyerShip",
  "店铺优惠": "storeDiscount",
  "活动优惠": "promoDiscount",
  "活动提报优惠": "reportDiscount",
  "履约服务费": "performanceFee",
  "佣金": "commission",
  "消费税": "saleTax",
  "欧洲VAT": "euVat",
  "墨西哥税费": "mxTax",
  "销售价格": "salePrice",
  "下单": "ordered",
  "付款": "paid",
  "签收": "signed",
  "站点": "site",
  "订单数量": "orderQty",
  "订单号": "orderNo",
  "订单状态": "orderStatus",
  "订单类型": "orderType",
  "销售站点": "salesSite",
  "库存模式": "stockMode",
  "打单状态": "printStatus",
  "发货方式": "shipMethod",
  "下单方式": "placeMethod",
  "可选物流": "optionalLogistics",
  "原订单号": "originOrderNo",
  "是否COD": "isCod",
  "超限拆包": "overLimit",
  "销售范围": "salesArea",
  "店铺 code": "storeCode",
  "订单币种": "orderCurrency",
  "销售币种": "saleCurrency",
  "买家支付运费": "buyerPaidShip",
  "店铺优惠合计": "storeDiscountSum",
  "活动优惠合计": "promoDiscountSum",
  "活动提报优惠合计": "reportDiscountSum",
  "总佣金": "commissionSum",
  "总履约服务费": "performanceFeeSum",
  "总服务费": "serviceFeeSum",
  "总消费税": "saleTaxSum",
  "总欧洲VAT": "euVatSum",
  "总墨西哥税费": "mxTaxSum",
  "国家": "country",
  "省/州": "province",
  "城市": "city",
  "邮编": "postCode",
  "下单时间": "orderTime",
  "付款时间": "payTime",
  "下发时间": "allocateTime",
  "签收时间": "receiptTime",
  "信息更新时间": "msgUpdateTime",
  "打印面单时间": "printingTime",
  "预约发货时间": "scheduleDeliveryTime",
  "卖家交货时间": "sellerDeliveryTime",
  "物流取件时间": "pickUpTime",
  "要求打单时间": "requestPrintTime",
  "要求发货时间": "needDeliveryTime",
  "要求揽收时间": "requestHandoverTime",
  "要求签收时间": "requestSignTime",
  "拒收时间": "rejectionTime",
  "退款时间": "returnTime",
  "报损时间": "lossTime",
  "仓库交货时间": "warehouseDeliveryTime",
  "期望揽收时间": "expectedCollectTime",
  "正常订单": "typeNormal",
  "换货订单": "typeExchange",
  "认证仓转自发货": "typeCertifiedSelf",
  "认证仓订单": "typeCertified",
  "可处理": "printOk",
  "不可处理": "printNo",
  "未选发货方式": "logisticsUnset",
  "平台合作物流": "logisticsPlatform",
  "商家自发货": "logisticsSeller",
  "平台指定物流": "placePlatform",
  "在线下单": "placeOnline",
  "是": "yes",
  "否": "no",
  "TikTok自营库存": "stockTiktok",
  "TEMU自营库存": "stockTemu",
  "SHEIN自营库存": "stockShein",
  "备货到TikTok仓(SFS)": "stockSfsTiktok",
  "备货到TEMU仓(SFS)": "stockSfsTemu",
  "备货到SHEIN仓(SFS)": "stockSfsShein",
  "商家仓发货": "stockSeller",
  "全部": "tabAll",
  "待付款": "tabUnpaid",
  "保留中": "tabOnHold",
  "待发货": "tabAwaitShip",
  "部分发货": "tabPartialShip",
  "待揽收": "tabAwaitCollect",
  "运输中": "tabInTransit",
  "已送达": "tabDelivered",
  "已完成": "tabCompleted",
  "已取消": "tabCancelled",
  "待处理": "tabPending",
  "待TEMU发货": "tabTemuShip",
  "待SHEIN发货": "tabSheinShip",
  "已发货": "tabShipped",
  "已签收": "tabSigned",
  "用户已退款": "tabUserRefund",
  "已报损": "tabDamaged",
  "已拒收": "tabRejected",
  "已退款": "tabRefunded"
};

function tr(raw) {
  if (raw == null || raw === '') return raw;
  const key = ORDER_TEXT[raw];
  return key ? i18n.t(`orderList.${key}`, { ns: 'workbench' }) : raw;
}


export { displayValue, formatMoney, normalizeImageUrl };

export const ORDER_TYPE_LABELS = {
  1: '正常订单',
  2: '换货订单',
  4: '认证仓转自发货',
  5: '认证仓订单',
};

export const PRINT_ORDER_STATUS_LABELS = {
  1: '可处理',
  2: '不可处理',
};

export const ORDER_LOGISTICS_TYPE_LABELS = {
  0: '未选发货方式',
  1: '平台合作物流',
  2: '商家自发货',
};

export const ORDER_PLACE_TYPE_LABELS = {
  1: '平台指定物流',
  2: '在线下单',
};

export function salesStatusLabel(code) {
  return tr(mapLabel(SALES_ORDER_STATUS_LABELS, code) || (code == null || code === '' ? '' : String(code)));
}

export function stockModeLabelOf(code) {
  return tr(mapLabel(SALES_ORDER_STOCK_MODE_LABELS, code) || (code == null || code === '' ? '' : String(code)));
}

export function formatTemuTime(raw) {
  if (raw == null || raw === '') return '';
  const s = String(raw).trim();
  if (!s) return '';
  return s.replace('T', ' ').replace(/\.\d{3}.*/, '').replace(/\+\d{4}$/, '').trim();
}

/** 列表行商品摘要 */
export function listOrderProductSummary(row) {
  const list = row?.orderGoodsInfoList || row?.goodsInfoList || row?.orderGoodsList;
  const currency = pickField(row, 'saleCurrency', 'orderCurrency', 'currency') || '';
  const incomeText = formatMoney(
    currency,
    pickField(row, 'estimatedGrossIncome', 'orderAmount', 'productTotalPrice'),
  );
  if (!Array.isArray(list) || list.length === 0) {
    return {
      title: '',
      imageUrl: '',
      skc: '',
      merchantSku: '',
      supplierCode: '',
      goodsCount: 0,
      skuCode: '',
      incomeText,
    };
  }
  const first = list[0] || {};
  const skuCode = pickField(first, 'skuCode') || '';
  // 商家SKU 只用 sellerSku/supplierSku，禁止回落成平台 skuCode
  let merchantSku = pickField(first, 'sellerSku', 'supplierSku', 'merchantSku') || '';
  if (merchantSku && skuCode && String(merchantSku).toLowerCase() === String(skuCode).toLowerCase()) {
    merchantSku = '';
  }
  return {
    title: pickField(first, 'goodsTitle', 'spuName') || '',
    imageUrl: normalizeImageUrl(pickField(first, 'spuPicURL', 'spuPicUrl', 'imageUrl', 'goodsThumb')),
    skc: pickField(first, 'skc', 'skcName') || '',
    merchantSku,
    supplierCode: pickField(first, 'supplierCode', 'goodsSn') || '',
    skuCode,
    goodsCount: list.length,
    incomeText,
  };
}

/** 包裹/运单摘要（含同步入库的轨迹快照） */
export function listLogisticsSummary(row) {
  const packages = Array.isArray(row?.packageWaybillList) ? row.packageWaybillList : [];
  const tracks = Array.isArray(row?.ipeLogisticsTracks) ? row.ipeLogisticsTracks : [];
  const items = packages.map((pkg, index) => {
    const packageNo = String(pickField(pkg, 'packageNo') || '').trim();
    const waybillNo = String(pickField(pkg, 'waybillNo', 'deliveryNo') || '').trim();
    const carrier = String(pickField(pkg, 'carrier') || '').trim();
    const snap = tracks.find((t) => {
      const p = String(pickField(t, 'packageNo') || '').trim();
      const w = String(pickField(t, 'waybillNo') || '').trim();
      if (packageNo && p && p === packageNo) return true;
      if (waybillNo && w && w === waybillNo) return true;
      return false;
    }) || tracks[index] || null;
    const trackPayload = snap?.track || snap;
    const trackInfo = Array.isArray(trackPayload?.trackInfo)
      ? trackPayload.trackInfo
      : (Array.isArray(trackPayload) ? trackPayload : []);
    let latestNode = '';
    for (const info of trackInfo) {
      const tracking = Array.isArray(info?.tracking) ? info.tracking : [];
      if (!tracking.length) continue;
      const last = tracking[tracking.length - 1] || tracking[0];
      latestNode = pickField(last, 'nodeCodeName', 'description', 'nodeCode') || latestNode;
    }
    return {
      packageNo,
      waybillNo,
      carrier,
      latestNode,
      hasTrack: Boolean(snap && !snap.error && trackInfo.length),
      trackSnap: snap,
      error: snap?.error || '',
    };
  });
  return {
    items,
    count: items.length,
    hasAnyTrack: items.some((i) => i.hasTrack),
  };
}

/** 有有效价格才展示：空 / 缺失 / 数值为 0 均隐藏 */
function hasMeaningfulAmount(raw) {
  if (raw === undefined || raw === null || raw === '') return false;
  const text = String(raw).trim();
  if (!text || text === '-' || text === '—') return false;
  const n = Number(text.replace(/,/g, ''));
  if (Number.isFinite(n)) return n !== 0;
  return true;
}

/** 列表价格主字段（订单维度） */
export function listPriceRows(row) {
  const currency = pickField(row, 'saleCurrency', 'orderCurrency', 'currency') || '';
  const money = (v) => formatMoney(currency, v);
  return [
    { label: tr('预计总收入'), raw: pickField(row, 'estimatedGrossIncome') },
    { label: tr('商品总金额'), raw: pickField(row, 'productTotalPrice') },
    { label: tr('供货价合计'), raw: pickField(row, 'totalCostPrice') },
    { label: tr('买家运费'), raw: pickField(row, 'sellerShippingFee') },
    { label: tr('店铺优惠'), raw: pickField(row, 'storeDiscountTotalPrice') },
    { label: tr('活动优惠'), raw: pickField(row, 'promotionDiscountTotalPrice') },
    { label: tr('活动提报优惠'), raw: pickField(row, 'totalSettleCurrencyPromotionPrice') },
    { label: tr('履约服务费'), raw: pickField(row, 'totalPerformanceServiceCharge') },
    { label: tr('佣金'), raw: pickField(row, 'totalCommission') },
    { label: tr('消费税'), raw: pickField(row, 'totalSaleTax') },
    { label: tr('欧洲VAT'), raw: pickField(row, 'totalSellerRealTax') },
    { label: tr('墨西哥税费'), raw: pickField(row, 'totalMxTaxPrice') },
    { label: tr('销售价格'), raw: pickField(row, 'settleActuallyPrice') },
  ]
    .filter((r) => hasMeaningfulAmount(r.raw))
    .map((r) => ({ label: r.label, value: money(r.raw) }));
}

export function listPrimaryPrices(row) {
  const all = listPriceRows(row);
  return {
    primary: all.slice(0, 4),
    extra: all.slice(4),
  };
}

export function listDateRows(row) {
  return [
    { label: tr('下单'), value: formatTemuTime(pickField(row, 'orderTime', 'orderAllocateTime', 'orderCreateTime')) },
    { label: tr('付款'), value: formatTemuTime(pickField(row, 'paymentTime')) },
    { label: tr('签收'), value: formatTemuTime(pickField(row, 'orderReceiptTime')) },
  ];
}

export function listOrderInfoRows(row) {
  const goodsList = row?.orderGoodsInfoList || row?.goodsInfoList || row?.orderGoodsList;
  const goodsCount = Array.isArray(goodsList) ? goodsList.length : Number(row?.goodsCount) || 0;
  return [
    { label: tr('站点'), value: pickField(row, 'salesSite', 'site') },
    { label: tr('订单数量'), value: goodsCount > 0 ? `${goodsCount}` : '' },
  ].filter((r) => r.value);
}

export function detailBasicRows(row) {
  return [
    { label: tr('订单号'), value: pickField(row, 'orderNo', 'orderNumber') },
    { label: tr('订单状态'), value: salesStatusLabel(pickField(row, 'orderStatus')) },
    { label: tr('订单类型'), value: tr(mapLabel(ORDER_TYPE_LABELS, pickField(row, 'orderType'))) },
    { label: tr('销售站点'), value: pickField(row, 'salesSite', 'site') },
    { label: tr('库存模式'), value: stockModeLabelOf(pickField(row, 'stockMode')) },
    { label: tr('打单状态'), value: tr(mapLabel(PRINT_ORDER_STATUS_LABELS, pickField(row, 'printOrderStatus'))) },
    { label: tr('发货方式'), value: tr(mapLabel(ORDER_LOGISTICS_TYPE_LABELS, pickField(row, 'orderLogisticsType'))) },
    { label: tr('下单方式'), value: tr(mapLabel(ORDER_PLACE_TYPE_LABELS, pickField(row, 'orderPlaceType'))) },
    { label: tr('可选物流'), value: displayValue(pickField(row, 'optionalLogisticsList')) },
    { label: tr('原订单号'), value: pickField(row, 'billNo') },
    {
      label: tr('是否COD'),
      value: pickField(row, 'isCod') === 1 || pickField(row, 'isCod') === '1'
        ? tr(fixedUi("是"))
        : (pickField(row, 'isCod') ? tr(fixedUi("否")) : ''),
    },
    {
      label: tr('超限拆包'),
      value: pickField(row, 'isOverLimitOrder') === 1 || pickField(row, 'isOverLimitOrder') === '1' ? '是' : '',
    },
    {
      label: tr('销售范围'),
      value: pickField(row, 'salesArea') === 1 || pickField(row, 'salesArea') === '1'
        ? '本土'
        : (pickField(row, 'salesArea') === 2 || pickField(row, 'salesArea') === '2' ? '跨境' : ''),
    },
    { label: tr('店铺 code'), value: pickField(row, 'storeCode') },
  ];
}

export function detailTimeRows(row) {
  const keys = [
    ['下单时间', 'orderTime'],
    ['付款时间', 'paymentTime'],
    ['下发时间', 'orderAllocateTime'],
    ['签收时间', 'orderReceiptTime'],
    ['信息更新时间', 'orderMsgUpdateTime'],
    ['打印面单时间', 'printingTime'],
    ['预约发货时间', 'scheduleDeliveryTime'],
    ['卖家交货时间', 'sellerDeliveryTime'],
    ['物流取件时间', 'pickUpTime'],
    ['要求打单时间', 'requestPrintTime'],
    ['要求发货时间', 'needDeliveryTime'],
    ['要求揽收时间', 'requestHandoverTime'],
    ['要求签收时间', 'requestSignTime'],
    ['拒收时间', 'orderRejectionTime'],
    ['退款时间', 'orderReturnTime'],
    ['报损时间', 'orderReportedLossTime'],
    ['仓库交货时间', 'warehouseDeliveryTime'],
    ['期望揽收时间', 'expectedCollectTime'],
  ];
  return keys.map(([label, key]) => ({
    label: tr(label),
    value: formatTemuTime(pickField(row, key)),
  }));
}

export function detailPriceRows(row) {
  const currency = pickField(row, 'saleCurrency', 'orderCurrency', 'currency') || '';
  const money = (v) => formatMoney(currency, v);
  const currencyRows = [
    { label: tr('订单币种'), value: pickField(row, 'orderCurrency') },
    { label: tr('销售币种'), value: pickField(row, 'saleCurrency') },
  ].filter((r) => r.value);
  const amountRows = [
    { label: tr('预计总收入'), raw: pickField(row, 'estimatedGrossIncome') },
    { label: tr('商品总金额'), raw: pickField(row, 'productTotalPrice') },
    { label: tr('供货价合计'), raw: pickField(row, 'totalCostPrice') },
    { label: tr('销售价格'), raw: pickField(row, 'settleActuallyPrice') },
    { label: tr('买家支付运费'), raw: pickField(row, 'sellerShippingFee') },
    { label: tr('店铺优惠合计'), raw: pickField(row, 'storeDiscountTotalPrice') },
    { label: tr('活动优惠合计'), raw: pickField(row, 'promotionDiscountTotalPrice') },
    { label: tr('活动提报优惠合计'), raw: pickField(row, 'totalSettleCurrencyPromotionPrice') },
    { label: tr('总佣金'), raw: pickField(row, 'totalCommission') },
    { label: tr('总履约服务费'), raw: pickField(row, 'totalPerformanceServiceCharge') },
    { label: tr('总服务费'), raw: pickField(row, 'totalServiceCharge') },
    { label: tr('总消费税'), raw: pickField(row, 'totalSaleTax') },
    { label: tr('总欧洲VAT'), raw: pickField(row, 'totalSellerRealTax') },
    { label: tr('总墨西哥税费'), raw: pickField(row, 'totalMxTaxPrice') },
  ]
    .filter((r) => hasMeaningfulAmount(r.raw))
    .map((r) => ({ label: r.label, value: money(r.raw) }));
  return [...currencyRows, ...amountRows];
}

export function detailReceiveRows(row) {
  const msg = row?.receiveMsg && typeof row.receiveMsg === 'object' ? row.receiveMsg : {};
  return [
    { label: tr('国家'), value: pickField(msg, 'country') },
    { label: tr('省/州'), value: pickField(msg, 'province') },
    { label: tr('城市'), value: pickField(msg, 'city') },
    { label: tr('邮编'), value: pickField(msg, 'postCode') },
  ];
}

export function trackInfoList(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.trackInfo)) return payload.trackInfo;
  if (Array.isArray(payload?.info?.trackInfo)) return payload.info.trackInfo;
  if (Array.isArray(payload?.track?.trackInfo)) return payload.track.trackInfo;
  if (Array.isArray(payload)) return payload;
  return [];
}

export function formatTrackTime(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '—';
  try {
    return new Date(n).toLocaleString(i18n.language || 'zh-CN', { hour12: false });
  } catch {
    return String(ms);
  }
}

export const DETAIL_KNOWN_TOP_KEYS = new Set([
  'orderNo', 'orderNumber', 'orderStatus', 'orderType', 'salesSite', 'site', 'stockMode',
  'printOrderStatus', 'orderLogisticsType', 'orderPlaceType', 'optionalLogisticsList',
  'billNo', 'isCod', 'isOverLimitOrder', 'salesArea', 'storeCode',
  'orderTime', 'paymentTime', 'orderAllocateTime', 'orderReceiptTime', 'orderMsgUpdateTime',
  'printingTime', 'scheduleDeliveryTime', 'sellerDeliveryTime', 'pickUpTime',
  'requestPrintTime', 'needDeliveryTime', 'requestHandoverTime', 'requestSignTime',
  'orderRejectionTime', 'orderReturnTime', 'orderReportedLossTime', 'warehouseDeliveryTime',
  'expectedCollectTime', 'orderCurrency', 'saleCurrency', 'currency', 'orderAmount',
  'estimatedGrossIncome', 'productTotalPrice', 'totalCostPrice', 'settleActuallyPrice',
  'sellerShippingFee', 'storeDiscountTotalPrice', 'promotionDiscountTotalPrice',
  'totalSettleCurrencyPromotionPrice', 'totalCommission', 'totalPerformanceServiceCharge',
  'totalServiceCharge', 'totalSaleTax', 'totalSellerRealTax', 'totalMxTaxPrice',
  'receiveMsg', 'orderGoodsInfoList', 'packageWaybillList', 'paymentInfo',
  'ipeLogisticsTracks', 'unProcessReason', 'orderTag', 'orderLabels', 'performanceType',
  'performanceBillingType', 'deliveryServiceType', 'lastSyncedAt', 'id',
]);
